// FILE: messageSelectionActions.ts
// Purpose: Decide whether a text selection inside the transcript is worth offering actions for,
//   and which message it belongs to.
// Layer: Web chat presentation logic
// Exports: the selection candidate/result shapes and the pure resolver.

import { MessageId } from "@taskin-code/contracts";
import { normalizeQuotedContextText } from "~/lib/quotedContext";

// What the DOM side reads off the selection before any judgement is applied. Keeping it as plain
// data is what lets the rules below be tested without a DOM.
export interface MessageSelectionCandidate {
  messageId: string | null;
  messageRole: string | null;
  text: string;
  isCollapsed: boolean;
}

export interface MessageSelection {
  messageId: MessageId;
  messageRole: "assistant" | "user";
  text: string;
}

// Below this a selection is almost certainly a stray click-drag rather than an intent to quote.
const MIN_SELECTION_LENGTH = 2;

function resolveMessageRole(value: string | null): "assistant" | "user" | null {
  return value === "assistant" || value === "user" ? value : null;
}

export function resolveMessageSelection(
  candidate: MessageSelectionCandidate | null,
): MessageSelection | null {
  if (!candidate || candidate.isCollapsed) {
    return null;
  }
  if (candidate.messageId === null || candidate.messageId.length === 0) {
    return null;
  }
  const messageRole = resolveMessageRole(candidate.messageRole);
  if (messageRole === null) {
    return null;
  }
  // Same normalization the quote itself will get, so what we validate is what gets sent.
  const text = normalizeQuotedContextText(candidate.text);
  if (text.trim().length < MIN_SELECTION_LENGTH) {
    return null;
  }
  return {
    messageId: MessageId.makeUnsafe(candidate.messageId),
    messageRole,
    text,
  };
}
